import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotificationsByUser, useNotificationsByRole, useMarkAsRead, useMarkAllAsReadByUser, useMarkAllAsReadByRole } from '@/hooks/useNotifications'
import { getNotificationIcon, getNotificationColor, formatTimeAgo } from '@/types/notification'
import type { Notification } from '@/types/notification'

interface NotificationListProps {
  userId?: string
  role?: string
  limit?: number
  showMarkAllRead?: boolean
  compact?: boolean
}

export const NotificationList: React.FC<NotificationListProps> = ({
  userId,
  role,
  limit = 10,
  showMarkAllRead = true,
  compact = false,
}) => {
  const navigate = useNavigate()

  // Fetch notifications based on userId or role
  const { data: userNotifications, isLoading: isLoadingUser } = useNotificationsByUser(
    userId || '',
    { limit }
  )
  const { data: roleNotifications, isLoading: isLoadingRole } = useNotificationsByRole(
    role || '',
    { limit }
  )

  // Mutations
  const markAsRead = useMarkAsRead()
  const markAllAsReadByUser = useMarkAllAsReadByUser()
  const markAllAsReadByRole = useMarkAllAsReadByRole()

  // Determine which data to use
  const isLoading = userId ? isLoadingUser : isLoadingRole
  const notifications = userId
    ? ((userNotifications as { notifications?: Notification[] })?.notifications || [])
    : ((roleNotifications as { notifications?: Notification[] })?.notifications || [])

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.isRead) {
      try {
        await markAsRead.mutateAsync(notification.id)
      } catch (error) {
        // Silently ignore error - don't block navigation
      }
    }

    // Navigate to actionLink if available
    if (notification.actionLink) {
      navigate(notification.actionLink)
    }
  }

  const handleMarkAllAsRead = async () => {
    if (userId) {
      await markAllAsReadByUser.mutateAsync(userId)
    } else if (role) {
      await markAllAsReadByRole.mutateAsync(role)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-20 bg-slate-100 dark:bg-white/5 rounded-xl animate-pulse"
          />
        ))}
      </div>
    )
  }

  if (notifications.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="size-12 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 text-[24px]">
            notifications_off
          </span>
        </div>
        <p className="text-sm font-bold text-slate-400 dark:text-slate-500">
          Tidak ada notifikasi
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-black text-slate-900 dark:text-white">
          Notifikasi Terbaru
        </h3>
        {showMarkAllRead && notifications.some((n: Notification) => !n.isRead) && (
          <button
            onClick={handleMarkAllAsRead}
            disabled={markAllAsReadByUser.isPending || markAllAsReadByRole.isPending}
            className="text-xs font-bold text-primary hover:underline disabled:opacity-50"
          >
            Tandai Semua Dibaca
          </button>
        )}
      </div>

      {/* Notification Items */}
      <div className="space-y-2">
        {notifications.map((notification: Notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClick={() => handleNotificationClick(notification)}
            compact={compact}
            isMarking={markAsRead.isPending}
          />
        ))}
      </div>
    </div>
  )
}

interface NotificationItemProps {
  notification: Notification
  onClick: () => void
  compact?: boolean
  isMarking?: boolean
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  compact = false,
  isMarking = false,
}) => {
  const icon = getNotificationIcon(notification.type)
  const colorClass = getNotificationColor(notification.type)

  if (compact) {
    return (
      <button
        onClick={onClick}
        disabled={isMarking}
        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all hover:bg-slate-50 dark:hover:bg-white/5 ${
          !notification.isRead ? 'bg-primary/5 border-l-3 border-primary' : ''
        } disabled:opacity-50`}
      >
        <div className={`size-8 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
          <span className={`material-symbols-outlined text-[16px] ${!notification.isRead ? 'filled' : ''}`}>{icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs truncate ${
            !notification.isRead ? 'font-black text-slate-900 dark:text-white' : 'font-bold text-slate-700 dark:text-slate-300'
          }`}>
            {notification.title}
          </p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
            {formatTimeAgo(notification.createdAt)}
          </p>
        </div>
        {!notification.isRead && (
          <div className="size-2.5 bg-primary rounded-full shrink-0 shadow-sm shadow-primary/50" />
        )}
      </button>
    )
  }

  return (
    <button
      onClick={onClick}
      disabled={isMarking}
      className={`w-full flex items-start gap-4 p-4 rounded-xl text-left transition-all hover:bg-slate-50 dark:hover:bg-white/5 ${
        !notification.isRead ? 'bg-primary/5 border-l-3 border-primary' : ''
      } disabled:opacity-50`}
    >
      <div className={`size-10 rounded-xl flex items-center justify-center shrink-0 ${colorClass}`}>
        <span className={`material-symbols-outlined text-[20px] ${!notification.isRead ? 'filled' : ''}`}>{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${
          !notification.isRead ? 'font-black text-slate-900 dark:text-white' : 'font-bold text-slate-700 dark:text-slate-300'
        }`}>
          {notification.title}
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-2">
          {formatTimeAgo(notification.createdAt)}
        </p>
      </div>
      {!notification.isRead && (
        <div className="size-2.5 bg-primary rounded-full shrink-0 mt-2 shadow-sm shadow-primary/50" />
      )}
    </button>
  )
}

export default NotificationList